import React, { useState, useEffect } from 'react';
import UserServices from '../../services/UserServices';
import CategoryServices from '../../services/CategoryServices';

function VendorUpdate({ vendor, closeModal, onSuccess }) {
    const [categories, setCategories] = useState([]);
    const [subCategories, setSubCategories] = useState([]);

    const [formValues, setFormValues] = useState({
        employeeType: '',
        name: '',
        email: '',
        mobileNo: '',
        category: '',           // ← we need this
        subCategory: '',
        stateName: '',
        cityName: '',
        address: '',
        pincode: '',            // ← added
        document: null,
        idImage: null,
    });

    const [previewImages, setPreviewImages] = useState({
        document: 'img/placeholder-img.png',
        idImage: 'img/placeholder-img.png',
    });

    // 1. Fill form when vendor data arrives
    useEffect(() => {
        if (vendor) {
            const initialCategory = ''; // will be set later
            const initialSubCategory = vendor?.subCategory?._id || '';

            setFormValues({
                employeeType: vendor?.employeeType || '',
                name: vendor?.name || '',
                email: vendor?.email || '',
                mobileNo: vendor?.mobileNo || '',
                category: initialCategory,
                subCategory: initialSubCategory,
                address: vendor?.address || '',
                stateName: vendor?.stateName || '',
                cityName: vendor?.cityName || '',
                pincode: vendor?.pincode?.[0] || '', // assuming first pincode
                document: null,
                idImage: null,
            });

            setPreviewImages({
                document: vendor.document
                    ? `${process.env.REACT_APP_URL}${vendor.document}`
                    : 'img/placeholder-img.png',
                idImage: vendor.idImage
                    ? `${process.env.REACT_APP_URL}${vendor.idImage}`
                    : 'img/placeholder-img.png',
            });
        }
    }, [vendor]);

    // 2. Load all active categories once
    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const res = await CategoryServices.getCategoryActive();
                if (res.status) {
                    setCategories(res.data || []);
                }
            } catch (error) {
                console.error('Error fetching categories:', error);
            }
        };
        fetchCategories();
    }, []);

    // 3. When category changes → load its subcategories
    useEffect(() => {
        if (formValues.category) {
            fetchSubcategories(formValues.category);
        } else {
            setSubCategories([]);
        }
    }, [formValues.category]);

    // 4. After categories are loaded + vendor exists → auto-select correct category
    useEffect(() => {
        if (!vendor || !categories.length || !vendor.subCategory?._id) return;

        const findParentCategory = async () => {
            try {
                // Option A: If backend gives subCategory with parent reference → best
                // Option B: We loop through all categories and find who has this subcat
                for (const cat of categories) {
                    const subs = await CategoryServices.getSubCategoryByCategory(cat._id);
                    if (subs.status && Array.isArray(subs.data)) {
                        const found = subs.data.find(sub => sub._id === vendor.subCategory._id);
                        if (found) {
                            setFormValues(prev => ({
                                ...prev,
                                category: cat._id,
                                // subCategory already set in first useEffect
                            }));
                            return;
                        }
                    }
                }
            } catch (err) {
                console.error("Could not auto-detect parent category", err);
            }
        };

        findParentCategory();
    }, [categories, vendor]);

    const fetchSubcategories = async (categoryId) => {
        if (!categoryId) return;
        try {
            const res = await CategoryServices.getSubCategoryByCategory(categoryId);
            if (res.status && Array.isArray(res.data)) {
                setSubCategories(res.data);
            } else {
                setSubCategories([]);
            }
        } catch (error) {
            console.error('Error fetching subcategories:', error);
            setSubCategories([]);
        }
    };

    const handleInputChange = (event) => {
        const { name, value } = event.target;
        setFormValues(prev => ({ ...prev, [name]: value }));
    };

    const handleFileChange = (event) => {
        const { name } = event.target;
        const file = event.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onloadend = () => {
            setPreviewImages(prev => ({
                ...prev,
                [name]: reader.result,
            }));
        };
        reader.readAsDataURL(file);

        setFormValues(prev => ({
            ...prev,
            [name]: file,
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            const formData = new FormData();

            // Append all text fields
            for (const key in formValues) {
                if (formValues[key] != null && key !== 'document' && key !== 'idImage') {
                    formData.append(key, formValues[key]);
                }
            }

            if (formValues.document) {
                formData.append('document', formValues.document);
            }
            if (formValues.idImage) {
                formData.append('idImage', formValues.idImage);
            }

            const response = await UserServices.updateVendor(vendor.id, formData);

            alert('Vendor updated successfully');
            onSuccess();
            closeModal();
        } catch (error) {
            console.error('Update failed:', error);
            alert('Failed to update vendor');
        }
    };

    return (
        <div className="modal fade edit-box show d-block" tabIndex={-1} aria-hidden="true">
            <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">Edit Vendor</h5>
                        <button type="button" className="btn-close" onClick={closeModal} />
                    </div>

                    <div className="modal-body">
                        <div className="container-box px-5">
                            <form onSubmit={handleSubmit}>
                                <div className="row">

                                    <div className="col-lg-6 col-sm-6">
                                        <div className="input-field">
                                            <label>Category*</label>
                                            <select
                                                name="category"
                                                className="form-control"
                                                value={formValues.category}
                                                onChange={handleInputChange}
                                                required
                                            >
                                                <option value="">Select Category</option>
                                                {categories.map(cat => (
                                                    <option key={cat._id} value={cat._id}>
                                                        {cat.title}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>

                                    <div className="col-lg-6">
                                        <div className="input-field">
                                            <label>Sub Category*</label>
                                            <select
                                                name="subCategory"
                                                className="form-control"
                                                value={formValues.subCategory}
                                                onChange={handleInputChange}
                                                disabled={!formValues.category || subCategories.length === 0}
                                                required
                                            >
                                                <option value="">Select Sub Category</option>
                                                {subCategories.map(sub => (
                                                    <option key={sub._id} value={sub._id}>
                                                        {sub.title}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>

                                    {/* Rest of your fields */}
                                    <div className="col-lg-6 col-sm-6">
                                        <div className="input-field">
                                            <label>User Type</label>
                                            <input
                                                type="text"
                                                name="employeeType"
                                                className="form-control"
                                                value={formValues.employeeType}
                                                readOnly
                                            />
                                        </div>
                                    </div>

                                    <div className="col-lg-6 col-sm-6">
                                        <div className="input-field">
                                            <label>Name</label>
                                            <input
                                                type="text"
                                                name="name"
                                                className="form-control"
                                                value={formValues.name}
                                                onChange={handleInputChange}
                                                required
                                            />
                                        </div>
                                    </div>

                                    <div className="col-lg-6 col-sm-6">
                                        <div className="input-field">
                                            <label>Mobile No</label>
                                            <input
                                                type="text"
                                                name="mobileNo"
                                                className="form-control"
                                                value={formValues.mobileNo}
                                                readOnly
                                                required
                                            />
                                        </div>
                                    </div>

                                    <div className="col-lg-6 col-sm-6">
                                        <div className="input-field">
                                            <label>Email</label>
                                            <input
                                                type="email"
                                                name="email"
                                                className="form-control"
                                                value={formValues.email}
                                                onChange={handleInputChange}
                                                required
                                            />
                                        </div>
                                    </div>

                                    <div className="col-lg-6 col-sm-6">
                                        <div className="input-field">
                                            <label>State</label>
                                            <input
                                                type="text"
                                                name="stateName"
                                                className="form-control"
                                                value={formValues.stateName}
                                                onChange={handleInputChange}
                                            />
                                        </div>
                                    </div>

                                    <div className="col-lg-6 col-sm-6">
                                        <div className="input-field">
                                            <label>City</label>
                                            <input
                                                type="text"
                                                name="cityName"
                                                className="form-control"
                                                value={formValues.cityName}
                                                onChange={handleInputChange}
                                            />
                                        </div>
                                    </div>

                                    <div className="col-lg-6 col-sm-6">
                                        <div className="input-field">
                                            <label>Pincode</label>
                                            <input
                                                type="text"
                                                name="pincode"
                                                className="form-control"
                                                value={formValues.pincode}
                                                onChange={handleInputChange}
                                            />
                                        </div>
                                    </div>

                                    <div className="col-lg-12 col-sm-12">
                                        <div className="input-field">
                                            <label>Address</label>
                                            <input
                                                type="text"
                                                name="address"
                                                className="form-control"
                                                value={formValues.address}
                                                onChange={handleInputChange}
                                            />
                                        </div>
                                    </div>

                                    {/* Uncomment if you want file upload back */}
                                    {/* document & idImage fields here... */}

                                </div>

                                <button type="submit" className="submit-green-btn mt-4">
                                    Submit
                                </button>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default VendorUpdate;